/**
 * Payment information page
 * @packageDocumentation
 */
import React from 'react';
import { Box, useTheme, Typography, Container } from '@mui/material';
import { Footer, NavigationBar } from 'Components';
import { getWindowDimensions } from 'Utils/useWindowDimensions';
import payment_logos from 'Assets/payment_logos.svg';

export default function PaymentsInfoPage() {
  const theme = useTheme();
  const { width, height } = getWindowDimensions();
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <NavigationBar tabIndex={false} />
      
      <Box
        component="main"
        sx={{
          flex: 1,
          mt: `${theme.spacing(3)}`,
          pb: `${theme.spacing(2)}`,
          overflowX: 'hidden'
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Правила оплаты */}
          <Typography variant="h4" fontWeight="bold" gutterBottom mt={4}>
            Правила оплаты
          </Typography>
          <Typography paragraph>
            К оплате принимаются платежные карты: VISA Inc, MasterCard WorldWide.
          </Typography>
          <Box
            component="img"
            src={payment_logos}
            sx={{ 
              width: { xs: '280px', md: '500px' },
              height: 'auto',
              margin: theme.spacing(2, 'auto'),
            }}
          />
          <Typography paragraph>
            Для оплаты услуг банковской картой при оформлении заказа в интернет-магазине выберите способ оплаты: банковской картой в разделе оплата.
          </Typography>
          <Typography paragraph>
            При оплате заказа банковской картой, обработка платежа происходит на странице авторизации банка, где Вам необходимо ввести данные Вашей банковской карты:
          </Typography>
          <ol>
            <li><Typography component="span">тип карты</Typography></li>
            <li><Typography component="span">номер карты</Typography></li>
            <li><Typography component="span">срок действия карты (указан на лицевой стороне карты)</Typography></li>
            <li><Typography component="span">Имя держателя карты (латинскими буквами, точно также как указано на карте) CVC2/CVV2 код</Typography></li>
          </ol>
          <Typography paragraph>
            Если Ваша карта подключена к услуге 3D-Secure, Вы будете автоматически переадресованы на страницу банка, выпустившего карту, для прохождения процедуры аутентификации. Информацию о правилах и методах дополнительной идентификации уточняйте в Банке, выдавшем Вам банковскую карту.
          </Typography>
          <Typography paragraph>
            Безопасность обработки интернет-платежей через платежный шлюз банка гарантирована международным сертификатом безопасности PCI DSS. Передача информации происходит с применением технологии шифрования SSL. Эта информация недоступна посторонним лицам.
          </Typography>

          {/* Советы и рекомендации */}
          <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
            Советы и рекомендации по необходимым мерам безопасности проведения платежей с использованием банковской карты:
          </Typography>
          <ol>
            <li><Typography component="span">берегите свои пластиковые карты так же, как бережете наличные деньги. Не забывайте их в машине, ресторане, магазине и т.д.</Typography></li>
            <li><Typography component="span">никогда не передавайте полный номер своей кредитной карты по телефону каким-либо лицам или компаниям</Typography></li>
            <li><Typography component="span">всегда имейте под рукой номер телефона для экстренной связи с банком, выпустившим вашу карту, и в случае ее утраты немедленно свяжитесь с банком</Typography></li>
            <li><Typography component="span">вводите реквизиты карты только при совершении покупки. Никогда не указывайте их по каким-то другим причинам.</Typography></li>
          </ol>

          {/* Отказ от услуги */}
          <Typography variant="h4" fontWeight="bold" gutterBottom mt={4}>
            Отказ от услуги
          </Typography>
          <Typography paragraph>
            Право потребителя на расторжение договора об оказании услуги регламентируется статьей 32 федерального закона «О защите прав потребителей»
          </Typography>
          <ol>
            <li><Typography component="span">Потребитель вправе расторгнуть договор об оказании услуги в любое время, уплатив исполнителю часть цены пропорционально части оказанной услуги до получения извещения о расторжении указанного договора и возместив исполнителю расходы, произведенные им до этого момента в целях исполнения договора, если они не входят в указанную часть цены услуги;</Typography></li>
            <li><Typography component="span">Потребитель при обнаружении недостатков оказанной услуги вправе по своему выбору потребовать:
              <ol>
                <li><Typography component="span">безвозмездного устранения недостатков;</Typography></li>
                <li><Typography component="span">соответствующего уменьшения цены;</Typography></li>
                <li><Typography component="span">возмещения понесенных им расходов по устранению недостатков своими силами или третьими лицами;</Typography></li>
              </ol>
            </Typography></li>
            <li><Typography component="span">Потребитель вправе предъявлять требования, связанные с недостатками оказанной услуги, если они обнаружены в течение гарантийного срока, а при его отсутствии в разумный срок, в пределах двух лет со дня принятия оказанной услуги;</Typography></li>
            <li><Typography component="span">Исполнитель отвечает за недостатки услуги, на которую не установлен гарантийный срок, если потребитель докажет, что они возникли до ее принятия им или по причинам, возникшим до этого момента;</Typography></li>
          </ol>

          {/* Порядок оплаты */}
          <Typography variant="h4" fontWeight="bold" gutterBottom mt={4}>
            Порядок оплаты и возврат средств
          </Typography>
          <Typography paragraph>
            Оплата печати каждого документа происходит с личного счёта пользователя в системе. Зарегистрированные пользователи могут пополнить свой счёт с помощью любого из предложенных методов оплаты. При отправке документа на печать нажатие на кнопку "печать" в форме печати стоимость документа резервируется на счету. При успешной печати зарезервированные средства снимаются с личного счёта. Если в течение 30 минут с момента подтверждения документ не будет напечатан по каким-либо причинам, зарезервированная стоимость документа будет возвращена на личный счёт. Напечатанный документ пользователь должен забрать с выбранного при печати принтера.
          </Typography>
          <Typography paragraph>
            В случае, если документ был напечатан в двухчасовой срок, но качество печати неудовлетворительное из-за неисправности принтера, пользователю рекомендуется обратиться по электронной почте info@printmipt.ru. При подтверждении неисправности принтера и неудовлетворительного качества печати стоимость печати документа будет возвращена на личный счёт пользователя в течение трёх дней.
          </Typography>

          {/* Расчет стоимости */}
          <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
            Рассчет стоимости печати
          </Typography>
          <Typography paragraph>
            Стоимость печати составляет от 2.5 до 5 рублей за лист для черно-белой печати и от 5 до 7.5 рублей за лист для цветной печати.
            В стоимость печати включены две составляющие- стоимость бумаги и тонера. Формула рассчета:
          </Typography>
          <Typography paragraph>
            Стоимость тонера = процент заполнения х 0.05р (процент заполнения &lt; 30%), 2.5р (процент заполнения &gt; 30%)
          </Typography>
          <Typography paragraph>
            Стоимость бумаги = 2.5р (для черно-белой печати), 5р (для цветной печати)
          </Typography>
          <Typography paragraph>
            Стоимость печати = (Стоимость тонера + Стоимость бумаги) х количество листов
          </Typography>
          <Typography paragraph>
            * для двусторонней печати стоимость тонера х 2
          </Typography>

          {/* Реквизиты */}
          <Typography variant="h4" fontWeight="bold" gutterBottom mt={4}>
            Реквизиты
          </Typography>
          <Box component="table" sx={{
            borderCollapse: 'collapse',
            border: '1px solid',
            borderColor: 'divider',
            margin: theme.spacing(2, 'auto'),
            width: '100%',
            '& td': {
              padding: theme.spacing(1, 2),
              border: '1px solid',
              borderColor: 'divider',
              '&:first-of-type': {
                  width: '25%',
                  fontWeight: 'bold',
              }
            }
          }}>
            <tbody>
              <tr>
                <td><Typography>Название</Typography></td>
                <td><Typography>ИП Костылев Владислав Викторович</Typography></td>
              </tr>
              <tr>
                <td><Typography>Адрес</Typography></td>
                <td><Typography>141707, Россия, Московская область, г. Долгопрудный, ш. Лихачевское, д. 1, корп. 2, кв. 93</Typography></td>
              </tr>
              <tr>
                <td><Typography>ОГРНИП</Typography></td>
                <td><Typography>325508100276408</Typography></td>
              </tr>
              <tr>
                <td><Typography>ИНН</Typography></td>
                <td><Typography>504717694554</Typography></td>
              </tr>
              <tr>
                <td><Typography>Телефон</Typography></td>
                <td><Typography>+7 (916) 015-84-00</Typography></td>
              </tr>
              <tr>
                <td><Typography>Почта</Typography></td>
                <td><Typography>info@printmipt.ru</Typography></td>
              </tr>
            </tbody>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}